<script lang="ts">
  import { defineComponent } from '@vue/composition-api';

  export default defineComponent({
    name: 'FCSelect',
    props: {
      value: {
        type: String,
        default: '',
      },
      name: {
        type: String,
        default: '',
      },
      autocomplete: {
        type: String,
        default: '',
      },
      label: {
        type: String,
        required: true,
      },
      options: {
        type: Array,
        required: true,
      },
      error: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      required: {
        type: Boolean,
        default: false,
      },
      displayPlaceHolder: {
        type: Boolean,
        default: true,
      },
      placeholder: {
        type: String,
        default: '-- Please select a size --',
      },
      showIcon: {
        type: Boolean,
        default: false,
      },
    },
  });
</script>

<template>
  <div class="select-container">
    <label
      :for="name"
      class="select-label"
      :class="{ 'error': error }">
      {{ label }}
      <span v-if="required" class="required-asterisk">*</span>
    </label>
    <div class="select-wrapper">
      <svg
        v-if="showIcon"
        class="select-icon"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path
          d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
          fill="currentColor" />
      </svg>
      <select
        :id="name"
        :required="required"
        :disabled="disabled"
        :value="value"
        :autocomplete="autocomplete"
        :name="name"
        class="select"
        :class="{ 'select--error': error, 'select--disabled': disabled, 'select--with-icon': showIcon }"
        @change="$emit('input', $event.target.value)">
        <option
          v-if="displayPlaceHolder"
          class="select-option-placeholder"
          value="">
          {{ placeholder }}
        </option>
        <option
          v-for="option in options"
          :key="option.key"
          class="select-option"
          :value="option.key">
          {{ option.name }}
        </option>
      </select>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "../../styles/exports";

.select-container {
  width: 100%;
  margin-bottom: 1.5rem;
}

.select-label {
  display: block;
  margin-bottom: 0.5rem;
  color: $color-gray;
  font-size: get-font-size-level(5);
  font-weight: 500;

  &.error {
    color: $color-accent;
  }
}

.required-asterisk {
  color: $color-accent;
  margin-left: 0.25rem;
}

.select-wrapper {
  position: relative;
  width: 100%;
}

.select-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: $color-gray;
  pointer-events: none;
  z-index: 1;
}

.select {
  appearance: none;
  width: 100%;
  border-radius: 8px;
  border: 1px solid $fc-greyscale-400;
  padding: 1rem 3rem 1rem 1rem;
  color: $color-black;
  font-size: get-font-size-level(6);
  background-color: $color-white;
  cursor: pointer;
  transition: border-color 0.2s ease;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.12);

  &--with-icon {
    padding-left: 3.5rem;
  }

  &:focus {
    outline: none;
    border-color: $color-black;
  }

  &:disabled {
    background-color: $color-light-background;
    cursor: not-allowed;
  }

  // Dropdown arrow (black by default)
  background-image: url("data:image/svg+xml;utf8,<svg fill='black' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><polygon points='5.25,7.5 10,12.5 14.75,7.5' /></svg>");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1.25rem;

  &--error {
    border-color: $color-accent;
    color: $color-accent;
    background-image: url("data:image/svg+xml;utf8,<svg fill='%23ff0000' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><polygon points='5.25,7.5 10,12.5 14.75,7.5' /></svg>");
  }

  &--disabled {
    background-color: $color-light-background;
  }
}

.select-option-placeholder {
  color: $fc-greyscale-400;
}

.select-option {
  color: $color-black;
}
</style>
